import { useEffect, useState } from "react";

interface WikipediaOutput {
  extract: string;
  ns: number;
  pageid: number;
  title: string;
}

const queryParams =
  "origin=*&format=json&action=query&prop=extracts&exintro&explaintext&redirects=1";

const getStorageKey = (query: string, language: string): string =>
  `wikipedia-${encodeURIComponent(language)}-${encodeURIComponent(query)}`;

const getFromLocalStorage = (
  query: string,
  language: string
): WikipediaOutput | null => {
  try {
    const storageValue = window.localStorage.getItem(
      getStorageKey(query, language)
    );

    if (storageValue) {
      return JSON.parse(storageValue) as WikipediaOutput;
    }
  } catch {
    // Invalid JSON or other error
  }

  return null;
};

const saveToLocalStorage = (
  query: string,
  language: string,
  data: WikipediaOutput
): void => {
  window.localStorage.setItem(
    getStorageKey(query, language),
    JSON.stringify(data)
  );
};

const fetchFromApi = (
  query: string,
  language: string
): Promise<WikipediaOutput> =>
  fetch(
    `https://${language}.wikipedia.org/w/api.php?${queryParams}&titles=${query}&srprop=extracts,languagecode:${language}`
  )
    .then((data) => data.json())
    .then((data) => data.query.pages)
    .then((result) => Object.values(result))
    .then((result) => result[0] as WikipediaOutput);

const pendingRequests: Map<string, Promise<WikipediaOutput | null>> = new Map();

const fetchWikipedia = (
  query: string,
  language: string
): Promise<WikipediaOutput | null> => {
  const cached = getFromLocalStorage(query, language);

  if (cached) {
    return Promise.resolve(cached);
  }

  const cacheKey = getStorageKey(query, language);
  const pending = pendingRequests.get(cacheKey);

  if (pending) {
    return pending;
  }

  const request = fetchFromApi(query, language)
    .then((result) => {
      if (result && result.pageid) {
        saveToLocalStorage(query, language, result);
      }
      pendingRequests.delete(cacheKey);
      return result;
    })
    .catch(() => {
      pendingRequests.delete(cacheKey);
      return null;
    });

  pendingRequests.set(cacheKey, request);

  return request;
};

const useWikipedia = (
  query: string,
  language: string
): WikipediaOutput | null => {
  const [data, setData] = useState<WikipediaOutput | null>(() =>
    getFromLocalStorage(query, language)
  );

  useEffect(() => {
    let cancelled = false;

    fetchWikipedia(query, language).then((result) => {
      if (!cancelled) {
        setData(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [query, language]);

  return data;
};

export default useWikipedia;
